import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { api } from '../services/api';

const InfoAdicionalForm = ({ open, onClose, ticketCodigo, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [error, setError] = useState('');
  const [puedeEditar, setPuedeEditar] = useState(true);

  // token para este usuario, asi solo este usuario puede editar o ver la info
  const getUserToken = () => {
    let token = localStorage.getItem(`ticket_user_token_${ticketCodigo}`);
    if (!token) {
      token = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(`ticket_user_token_${ticketCodigo}`, token);
    }
    return token;
  };

  const fetchInformacion = async () => {
    if (!ticketCodigo) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.tickets.getInformacionAdicional(ticketCodigo);
      
      if (response.status === 'success') {
        setPreguntas(response.data.preguntas || []);
        setPuedeEditar(response.data.puede_editar);
        
        // si el ticket tiene rtas, ver si este usuario los cargo
        const userToken = getUserToken();
        const hasUserData = localStorage.getItem(`ticket_data_${ticketCodigo}_${userToken}`);
        
        if (!response.data.puede_editar && !hasUserData) {
          setError('Este ticket ya tiene información cargada por otro usuario');
          return;
        }
        
        // si hay rtas, cargarlas
        const respuestasExistentes = {};
        response.data.preguntas.forEach(pregunta => {
          if (pregunta.tiene_respuesta) {
            respuestasExistentes[pregunta.id] = pregunta.respuesta;
          }
        });
        setRespuestas(respuestasExistentes);
        
      } else {
        setError(response.message || 'Error al cargar información');
      }
    } catch (error) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchInformacion();
    }
  }, [open, ticketCodigo]);

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: valor
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    
    try {
      const userToken = getUserToken();
      const response = await api.tickets.saveInformacionAdicional(
        ticketCodigo,
        respuestas,
        userToken
      );
      
      if (response.status === 'success') {
        // seteamos en localstorage que este usuario guardo la info
        localStorage.setItem(`ticket_data_${ticketCodigo}_${userToken}`, 'true');
        
        onSuccess?.();
        onClose();
      } else {
        setError(response.message || 'Error al guardar información');
      }
    } catch (error) {
      setError('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    return preguntas.every(pregunta => 
      respuestas[pregunta.id] && respuestas[pregunta.id].trim().length > 0
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        fontFamily: 'Bricolage Grotesque',
        fontWeight: 600,
        color: '#0B0060',
        fontSize: '20px'
      }}>
        Completar Información Adicional
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#5656FF' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'Bricolage Grotesque' }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{
              mb: 3,
              color: '#6B7280',
              fontFamily: 'Bricolage Grotesque'
            }}>
              Por favor, completa la siguiente información para tu ticket:
            </Typography>
            
            {preguntas.map((pregunta, index) => (
              <TextField
                key={pregunta.id}
                fullWidth
                label={pregunta.informacion_adicional}
                value={respuestas[pregunta.id] || ''}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                /* disabled={!puedeEditar} */
                margin="normal"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Bricolage Grotesque',
                    '& fieldset': {
                      borderColor: '#E6E6FF',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5656FF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5656FF',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Bricolage Grotesque',
                    color: '#6B7280',
                    '&.Mui-focused': {
                      color: '#5656FF',
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          sx={{
            fontFamily: 'Bricolage Grotesque',
            color: '#6B7280',
            textTransform: 'none'
          }}
        >
          {preguntas.some(p => p.tiene_respuesta) ? 'Cerrar' : 'Cancelar'}
        </Button>
        
        {!error && (
          <Button 
            onClick={handleSubmit}
            disabled={saving || !isFormValid() || error}
            variant="contained"
            sx={{
              fontFamily: 'Bricolage Grotesque',
              backgroundColor: '#5656FF',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#0B0060'
              },
              '&:disabled': {
                backgroundColor: '#cccccc'
              }
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {saving ? 'Guardando...' : preguntas.some(p => p.tiene_respuesta) ? 'Actualizar' : 'Guardar Información'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InfoAdicionalForm;